
import {
  defineComponent,
  computed,
  PropType,
  ref,
  onMounted,
  onBeforeUnmount,
  nextTick
} from 'vue'
import { SENTIMENT_STAGES, getStageByScore } from '@/utils/sentiment/constants'
import type { SentimentThemeMode } from '@/utils/sentiment/constants'
import { getStageRowActiveBg } from '@/utils/sentiment/theme'

export default defineComponent({
  name: 'SentimentStageTable',
  props: {
    score: { type: Number, default: 0 },
    theme: { type: String as PropType<SentimentThemeMode>, default: 'dark' }
  },
  setup(props) {
    const wrapRef = ref<HTMLElement | null>(null)
    const boxRef = ref<HTMLElement | null>(null)
    const headRowHeight = ref(34)
    const bodyRowHeight = ref(0)
    let resizeTimer: ReturnType<typeof setTimeout> | null = null

    const stages = SENTIMENT_STAGES
    const activeTier = computed(() => getStageByScore(props.score).tier)
    const getRowActiveBg = (tier: number) =>
      getStageRowActiveBg(tier, props.theme)

    const measureTableLayout = () => {
      const box = boxRef.value
      if (!box) return
      const headRow = box.querySelector('thead tr') as HTMLElement | null
      const headH = headRow?.offsetHeight ?? 34
      headRowHeight.value = headH
      const avail = box.clientHeight - headH
      if (avail > 0) {
        bodyRowHeight.value = Math.max(32, Math.floor(avail / stages.length))
      }
    }

    const scheduleMeasure = () => {
      if (resizeTimer) clearTimeout(resizeTimer)
      resizeTimer = setTimeout(() => {
        resizeTimer = null
        nextTick(measureTableLayout)
      }, 50)
    }

    const getRowStyle = (tier: number) => {
      const style: Record<string, string> = {}
      if (bodyRowHeight.value > 0) {
        style.height = `${bodyRowHeight.value}px`
      }
      if (activeTier.value === tier) {
        style.background = getRowActiveBg(tier)
      }
      return style
    }

    onMounted(() => {
      scheduleMeasure()
      setTimeout(scheduleMeasure, 120)
      setTimeout(scheduleMeasure, 400)
      window.addEventListener('resize', scheduleMeasure)
    })

    onBeforeUnmount(() => {
      window.removeEventListener('resize', scheduleMeasure)
      if (resizeTimer) clearTimeout(resizeTimer)
    })

    return {
      wrapRef,
      boxRef,
      stages,
      activeTier,
      headRowHeight,
      getRowStyle
    }
  }
})
